import { toJS } from 'mobx';

import type { Address } from '~/api/DeliveryAddress';
import { cartStore, checkoutStore, orderStore, userStore } from '~/stores';
import type {
  V3DestinationMethod,
  V3PupSelection,
} from '~/stores/CartStore/CartStore';
import type { DeliveryAddress } from '~/stores/UserStore';
import { defaultAddressType } from '~/utils/addressName';

export type CheckoutDestinationMethod = V3DestinationMethod;

/**
 * Method the checkout pills show. It may run ahead of `destination.kind` in
 * `calculate`: pickup with no point chosen yet still calculates for the
 * customer address.
 */
export const checkoutDestinationMethod = (): CheckoutDestinationMethod =>
  cartStore.v3DestinationMethod;

export const deliveryAddressFromSaved = (
  address: Address,
): DeliveryAddress => ({
  zip: address.postcode || '',
  country: address.country_code || '',
  city: address.city || '',
  region: '',
  address1: address.street_address_1 || '',
  address2: address.street_address_2 ?? '',
  addressFloor: address.floor ?? '',
  addressBld: address.building ?? '',
  addressApt: address.apartment ?? '',
  type: address.type || defaultAddressType(),
  shortAddress: address.street_address_1 || '',
  coordinates: { lat: address.latitude, lng: address.longitude },
  placeId: address.place_id || '',
  addressId: address.id,
  comment: address.comment || '',
  instructions: toJS(address.instructions || []),
  usage: address.usage,
  warehouseCode: address.warehouse_code ?? undefined,
});

/** Resolves to `false` when no warehouse serves the address — nothing is applied then. */
export const applyCourierAddress = async (
  address: Address,
): Promise<boolean> => {
  const etaCalculation = await orderStore
    .fetchETA(`${address.latitude},${address.longitude}`, undefined, {
      destinationKind: 'customer',
    })
    .catch(() => null);

  if (!etaCalculation) {
    return false;
  }

  cartStore.selectV3Pup(null);
  userStore.setDeliveryAddress(deliveryAddressFromSaved(address));
  orderStore.setEtaCalculation(etaCalculation);
  checkoutStore.setDeliverySlots(etaCalculation.deliverySlots || null);

  return true;
};

export const applyPickupPoint = (pup: V3PupSelection): void => {
  cartStore.selectV3Pup(pup);
};
